import type { Field, TypeInstanceMap } from '@lighthouse/core'
import { VariableType } from '@lighthouse/core'
import type { FlowNode } from '@lighthouse/shared'
import {
    type DownloadFileActionConfig,
    getAllCanBeUpstreamNodes,
    getUpstreamNodesOptions,
    innerTypeToFieldTypeMapInCondition,
    isRichTextValue,
    useAtomData
} from '@lighthouse/shared'
import { useNodesData } from '@xyflow/react'
import React, { useMemo } from 'react'

import { workflowListAtom } from '@/atoms/workflow/state'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useGetUserRecordOptions } from '@/hooks/usePage'

import * as CM from '../styles'
import { textRichVariableName } from '../util'
import * as SC from './styles'

type DownloadFileCardProps = {
    config: DownloadFileActionConfig
}

const DownloadFileCard: React.FC<DownloadFileCardProps> = ({ config }) => {
    const { fileUrl } = config
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const { type } = useFlowContainerContext()
    const isInFlow = !!type

    const nodeId = useMemo(() => {
        if (fileUrl.type === 'UPSTREAM') {
            const nodeId = fileUrl?.upstreamVariable?.nodeId
            if (nodeId) {
                return nodeId
            }
        }
        return ''
    }, [fileUrl])

    const nodes = useNodesData([nodeId]) as FlowNode[]

    const upstreamOptions = useMemo(
        () =>
            isInFlow
                ? getUpstreamNodesOptions({
                      workflowList,
                      dataSourceList,
                      upstreamNodes: getAllCanBeUpstreamNodes(nodes || []),
                      allUpstreamNodes: nodes
                  })
                : undefined,
        [isInFlow, workflowList, dataSourceList, nodes]
    )

    const userOption = useGetUserRecordOptions()

    const text = useMemo(() => {
        if (fileUrl.type === VariableType.VALUE) {
            const result = fileUrl?.valueVariable?.value || ''
            if (typeof result === 'string') {
                return result
            }
            if (isRichTextValue(result)) {
                return ''
            }
            if (typeof result === 'object') {
                return result?.join(',')
            }
            return typeof result === 'string' ? result : JSON.stringify(result)
        }
        return textRichVariableName({
            userOption,
            upstreamOptions,
            // pageOptions: isNotAutomation ? [PAGE_LINK_OPTION, ...pageOptions] : undefined,
            data: fileUrl
        })
    }, [fileUrl, userOption, upstreamOptions])

    if (!text) {
        return null
    }

    return (
        <CM.CardItemContainer>
            <CM.FieldItemContent>
                <CM.FieldItemTitle
                    whiteSpace="pre-wrap"
                    color={fileUrl.type === VariableType.VALUE ? undefined : 'var(--color-purple-500)'}
                >
                    {text}
                </CM.FieldItemTitle>
            </CM.FieldItemContent>
        </CM.CardItemContainer>
    )
}

export default DownloadFileCard
